import { useCallback, useRef, useState, type MouseEvent, type ReactNode, type RefObject } from 'react';

import { X } from '../../../../../design-system/icons';
import { NO_TIMEOUT, useTimeout } from '../../../../../lib/browser/useTimeout';
import { useBodyScrollLock } from '../../../../../lib/useBodyScrollLock';
import { cn } from '../../../../../lib/utils';
import type { NotesSession } from '../session/useNotesSession';
import { FULL_PREVIEW_LABEL, Transport } from './Transport';
import { useCompactTheater } from './useCompactTheater';
import { useFocusTrap } from './useFocusTrap';
import type { Intrinsic, MediaController } from './useMediaController';

export type CorsMode = 'anonymous' | 'none';

const SEEK_FLASH_MS = 120;
/** Nothing has flashed yet. */
const NO_FLASH_NONCE = 0;
const DEFAULT_ASPECT = '9 / 16';
const CLOSE_ICON_PX = 18;
const CLOSE_FULL_VIEW_LABEL = 'Close (Esc)';

export interface MediaTheaterProps {
  controller: MediaController;
  /**
   * Anchored-notes mode. Without it the theater is the plain native player it
   * has always been (the non-notes surfaces still mount it that way).
   */
  session?: NotesSession;
  src: string;
  title: string;
  /** Bump to flash the stage white — the seek feedback. */
  seekFlashNonce: number;
  /** Rendered over the video (annotation surface, banners). */
  overlay?: ReactNode;
  /**
   * 'anonymous' loads with CORS so frames can be read into a canvas. A bucket
   * that sends no CORS headers makes the browser refuse the media entirely in
   * that mode, so the panel falls back to 'none' once a fresh URL fails too.
   */
  corsMode: CorsMode;
}

function corsAttribute(mode: CorsMode): 'anonymous' | undefined {
  return mode === 'anonymous' ? 'anonymous' : undefined;
}

function aspectRatioFor(intrinsic: Intrinsic | null): string {
  if (!intrinsic) return DEFAULT_ASPECT;
  return `${intrinsic.width} / ${intrinsic.height}`;
}

function SeekFlash({ nonce }: { nonce: number }) {
  // The nonce whose flash has faded; a newer one is lit until its timer runs out.
  const [fadedNonce, setFadedNonce] = useState(NO_FLASH_NONCE);
  const lit = nonce !== fadedNonce;
  const fade = useCallback(() => setFadedNonce(nonce), [nonce]);
  // Keyed on the nonce, so a seek during a flash restarts it rather than cutting it short.
  useTimeout(fade, lit ? SEEK_FLASH_MS : NO_TIMEOUT, nonce);
  return (
    <div
      aria-hidden="true"
      data-testid="seek-flash"
      className={cn(
        'pointer-events-none absolute inset-0 bg-white transition-opacity duration-150 motion-reduce:transition-none',
        lit ? 'opacity-60 motion-reduce:opacity-0' : 'opacity-0',
      )}
    />
  );
}

/** Close only: full view carries no format label. */
function FullViewHeader({ onClose, closeRef }: { onClose: () => void; closeRef: RefObject<HTMLButtonElement | null> }) {
  return (
    <div className="flex items-center justify-end px-1 py-[2px]">
      <button
        ref={closeRef}
        type="button"
        aria-label={CLOSE_FULL_VIEW_LABEL}
        onClick={onClose}
        className="flex size-[30px] items-center justify-center rounded-full text-white/70 transition-colors duration-150 hover:bg-white/[.14] hover:text-white motion-reduce:transition-none"
      >
        <X size={CLOSE_ICON_PX} />
      </button>
    </div>
  );
}

/**
 * The native player stage. Sized to the video's own aspect ratio so the
 * picture is never letterboxed inside its box — pointer maths on the
 * annotation overlay then works with a single scale factor.
 *
 * Full view does not mount a second player: the very same subtree is restyled
 * into a fixed layer, so the `<video>` element (and therefore the decoder, the
 * playhead and the controller's binding to it) survives opening and closing.
 */
export function MediaTheater({ controller, session, src, title, seekFlashNonce, overlay, corsMode }: MediaTheaterProps) {
  const theaterRef = useRef<HTMLDivElement | null>(null);
  const fullViewLayerRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const compact = useCompactTheater(theaterRef);
  // Read apart up front: the compiler treats an object whose member is passed
  // as `ref` as a ref itself, so `controller.intrinsic` in render would be
  // flagged as reading a ref.
  const { videoRef, intrinsic } = controller;
  const fullView = session?.fullView === true;
  const annotating = !!session?.capture;
  useBodyScrollLock(fullView);
  useFocusTrap(fullViewLayerRef, fullView, { initialFocusRef: closeButtonRef });

  const closeFullView = useCallback(() => {
    session?.setFullView(false);
  }, [session]);

  const onBackdropClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      closeFullView();
    },
    [closeFullView],
  );

  const video = (
    <video
      // The key forces a fresh element when the CORS mode flips — the
      // attribute is only honoured at load time. Full view deliberately does
      // not take part in the key, so toggling it never reloads the media.
      key={corsMode}
      ref={videoRef}
      src={src}
      crossOrigin={corsAttribute(corsMode)}
      playsInline
      controls={!session}
      controlsList="nodownload"
      className="size-full"
      title={title}
    />
  );

  if (!session) {
    return (
      <div className="relative w-full max-w-full overflow-hidden rounded-xl bg-black" style={{ aspectRatio: aspectRatioFor(intrinsic) }} data-testid="media-theater">
        {video}
        {overlay}
        <SeekFlash nonce={seekFlashNonce} />
      </div>
    );
  }

  return (
    <div
      ref={fullViewLayerRef}
      className={cn(fullView && 'fixed inset-0 z-[85] flex items-center justify-center bg-zinc-950/90 p-6')}
      data-testid={fullView ? 'full-view-layer' : undefined}
      role={fullView ? 'dialog' : undefined}
      aria-modal={fullView ? true : undefined}
      aria-label={fullView ? FULL_PREVIEW_LABEL : undefined}
      onClick={fullView ? onBackdropClick : undefined}
    >
      <div
        ref={theaterRef}
        data-testid="media-theater"
        className={cn(
          'relative overflow-hidden rounded-[18px] bg-(--background-player) text-stone-100 shadow-[inset_0_1px_0_rgba(255,255,255,.06)]',
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-[18px] after:shadow-[inset_0_0_0_1px_rgba(255,255,255,.07)]',
          fullView ? 'flex max-h-[94vh] flex-col gap-2.5 border border-white/15 p-3' : 'w-full px-[14px] pb-[10px] pt-[14px]',
        )}
      >
        {fullView && <FullViewHeader onClose={closeFullView} closeRef={closeButtonRef} />}

        <div
          className={cn(
            'relative overflow-hidden rounded-xl bg-black transition-shadow duration-200 motion-reduce:transition-none',
            annotating && 'shadow-[inset_0_0_0_2px_rgba(255,255,255,.92)]',
            // The stage is a <div> whose only child is percentage-sized, so
            // auto width and height would collapse it to nothing. `h-[74vh]`
            // supplies the definite size, `aspect-ratio` derives the width,
            // and `max-w-[88vw]` pulls both back for a wide 16:9 cut.
            fullView ? 'mx-auto h-[74vh] max-h-[74vh] min-h-0 max-w-[88vw]' : 'w-full',
          )}
          style={{ aspectRatio: aspectRatioFor(intrinsic) }}
          data-testid="media-stage"
        >
          {video}
          {overlay}
          <SeekFlash nonce={seekFlashNonce} />
        </div>

        {/* The frame being marked up is frozen, so a transport would only mislead. */}
        {!annotating && <Transport session={session} compact={compact && !fullView} src={src} crossOrigin={corsAttribute(corsMode)} />}
      </div>
    </div>
  );
}
